import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getVendorsByType, getCCByVendor, getPOForInvoiceReport, getVendorInvoices,
    getVendorInvoiceDetailSummary, getSupplierInvoiceSummary,
} from '../../api/ReportsAPI/vendorInvoiceReportAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchVendorsByType = createAsyncThunk('vendorInvoiceReport/fetchVendors', async (params, { rejectWithValue }) => {
    try { const res = await getVendorsByType(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch vendors')); }
});

export const fetchCCByVendor = createAsyncThunk('vendorInvoiceReport/fetchCCList', async (params, { rejectWithValue }) => {
    try { const res = await getCCByVendor(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});

export const fetchPOForInvoiceReport = createAsyncThunk('vendorInvoiceReport/fetchPOList', async (params, { rejectWithValue }) => {
    try { const res = await getPOForInvoiceReport(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch PO numbers')); }
});

export const fetchVendorInvoiceReport = createAsyncThunk('vendorInvoiceReport/fetchReport', async (params, { rejectWithValue }) => {
    try { const res = await getVendorInvoices(params); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch vendor invoice report')); }
});

export const fetchInvoiceDetail = createAsyncThunk('vendorInvoiceReport/fetchDetail', async ({ vendorType, ...params }, { rejectWithValue }) => {
    try {
        const res = vendorType === 'Service Provider'
            ? await getVendorInvoiceDetailSummary(params)
            : await getSupplierInvoiceSummary(params);
        return res?.Data || null;
    } catch (e) { return rejectWithValue(err(e, 'Failed to fetch invoice detail')); }
});

const initialState = {
    vendorList: [], vendorListLoading: false,
    ccList: [], ccListLoading: false,
    poList: [], poListLoading: false,
    report: null, reportLoading: false, reportError: null,
    detail: null, detailLoading: false, detailError: null,
};

const vendorInvoiceReportSlice = createSlice({
    name: 'vendorInvoiceReport',
    initialState,
    reducers: {
        clearVendorList: (s) => { s.vendorList = []; },
        clearCCList: (s) => { s.ccList = []; },
        clearPOList: (s) => { s.poList = []; },
        clearVendorInvoiceReport: (s) => { s.report = null; s.reportError = null; },
        clearInvoiceDetail: (s) => { s.detail = null; s.detailError = null; },
        resetVendorInvoiceReport: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVendorsByType.pending, (s) => { s.vendorListLoading = true; })
            .addCase(fetchVendorsByType.fulfilled, (s, a) => { s.vendorListLoading = false; s.vendorList = a.payload; })
            .addCase(fetchVendorsByType.rejected, (s) => { s.vendorListLoading = false; });

        builder
            .addCase(fetchCCByVendor.pending, (s) => { s.ccListLoading = true; })
            .addCase(fetchCCByVendor.fulfilled, (s, a) => { s.ccListLoading = false; s.ccList = a.payload; })
            .addCase(fetchCCByVendor.rejected, (s) => { s.ccListLoading = false; });

        builder
            .addCase(fetchPOForInvoiceReport.pending, (s) => { s.poListLoading = true; })
            .addCase(fetchPOForInvoiceReport.fulfilled, (s, a) => { s.poListLoading = false; s.poList = a.payload; })
            .addCase(fetchPOForInvoiceReport.rejected, (s) => { s.poListLoading = false; });

        builder
            .addCase(fetchVendorInvoiceReport.pending, (s) => { s.reportLoading = true; s.reportError = null; })
            .addCase(fetchVendorInvoiceReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
            .addCase(fetchVendorInvoiceReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; });

        builder
            .addCase(fetchInvoiceDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; })
            .addCase(fetchInvoiceDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload; })
            .addCase(fetchInvoiceDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; });
    },
});

export const {
    clearVendorList, clearCCList, clearPOList, clearVendorInvoiceReport, clearInvoiceDetail, resetVendorInvoiceReport,
} = vendorInvoiceReportSlice.actions;

export const selectVIRVendorList        = (s) => s.vendorInvoiceReport.vendorList;
export const selectVIRVendorListLoading = (s) => s.vendorInvoiceReport.vendorListLoading;
export const selectVIRCCList            = (s) => s.vendorInvoiceReport.ccList;
export const selectVIRCCListLoading     = (s) => s.vendorInvoiceReport.ccListLoading;
export const selectVIRPOList            = (s) => s.vendorInvoiceReport.poList;
export const selectVIRPOListLoading     = (s) => s.vendorInvoiceReport.poListLoading;
export const selectVIRReport            = (s) => s.vendorInvoiceReport.report;
export const selectVIRReportLoading     = (s) => s.vendorInvoiceReport.reportLoading;
export const selectVIRReportError       = (s) => s.vendorInvoiceReport.reportError;
export const selectVIRDetail            = (s) => s.vendorInvoiceReport.detail;
export const selectVIRDetailLoading     = (s) => s.vendorInvoiceReport.detailLoading;
export const selectVIRDetailError       = (s) => s.vendorInvoiceReport.detailError;

export default vendorInvoiceReportSlice.reducer;
